import type { DagLayoutMeta, DagLayoutOptions, DagLayoutRequest, DagLayoutWorkerResponse, DagViewerGraph } from "./types";
import { computeDagLayout } from "./layout";
import { computeCenteredLayout, computeFlowLayout } from "./viewLayouts";

interface LayoutCallbacks {
  onMeta: (meta: DagLayoutMeta, nodeCount: number) => void;
  onChunk: (start: number, positions: Float32Array) => void;
  onComplete: (durationMs: number) => void;
  onError: (message: string) => void;
}

export class DagLayoutWorkerClient {
  private readonly worker: Worker | null;
  private jobId = 0;
  private callbacks: LayoutCallbacks | null = null;

  constructor() {
    this.worker = typeof Worker === "undefined"
      ? null
      : new Worker(new URL("./layout.worker.ts", import.meta.url), { type: "module", name: "thread-dag-layout" });
    if (this.worker) {
      this.worker.onmessage = (event: MessageEvent<DagLayoutWorkerResponse>) => this.handleMessage(event.data);
      this.worker.onerror = (event) => this.callbacks?.onError(event.message || "DAG layout worker failed");
    }
  }

  layout(graph: DagViewerGraph, callbacks: LayoutCallbacks, options: DagLayoutOptions = {}): void {
    this.jobId += 1;
    this.callbacks = callbacks;
    const request: DagLayoutRequest = {
      type: "layout",
      jobId: this.jobId,
      graph: {
        nodes: graph.nodes.map(({ id, kind, type, semanticRole, timestamp, channel }) => ({
          id,
          kind,
          type,
          semanticRole,
          timestamp,
          channel,
        })),
        edges: graph.edges.map(({ source, target }) => ({ source, target })),
      },
      options,
    };
    if (this.worker) {
      this.worker.postMessage(request);
      return;
    }
    // SSR, unit tests, and hardened embeds may not expose Web Workers. Keep a
    // deterministic fallback without changing the surface contract.
    const currentJob = this.jobId;
    queueMicrotask(() => {
      if (currentJob !== this.jobId || !this.callbacks) return;
      const startedAt = performance.now();
      try {
        const result = options.mode === "flow"
          ? computeFlowLayout(request.graph)
          : options.mode === "centered"
            ? computeCenteredLayout(request.graph, options.anchorIds ?? [], options)
            : computeDagLayout(request.graph, options);
        const { positions, ...meta } = result;
        this.callbacks.onMeta(meta, request.graph.nodes.length);
        this.callbacks.onChunk(0, positions);
        this.callbacks.onComplete(performance.now() - startedAt);
      } catch (error) {
        this.callbacks.onError(error instanceof Error ? error.message : "DAG layout failed");
      }
    });
  }

  destroy(): void {
    this.callbacks = null;
    this.worker?.terminate();
  }

  private handleMessage(message: DagLayoutWorkerResponse): void {
    if (message.jobId !== this.jobId || !this.callbacks) return;
    if (message.type === "meta") this.callbacks.onMeta(message.meta, message.nodeCount);
    if (message.type === "chunk") this.callbacks.onChunk(message.start, message.positions);
    if (message.type === "complete") this.callbacks.onComplete(message.durationMs);
    if (message.type === "error") this.callbacks.onError(message.message);
  }
}
